<script setup lang="ts">
import { defineProps, computed } from "vue";
import { RelatedBlog } from "@/types/blogs";
import { ROUTE_NAMES } from "@/constants/routeNames";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useLangStore } from "@/stores/langStore";

const props = defineProps<{
  relatedBlogs: RelatedBlog[];
}>();

const isHidedButtons = true;

const prevBlog = computed(() => props.relatedBlogs?.[1] ?? null);
const nextBlog = computed(() => props.relatedBlogs?.[0] ?? null);

const langStore = useLangStore();
const { t } = useI18n();
const router = useRouter();

function openRelatedBlog(relatedBlog: RelatedBlog) {
  router.push({
    name: ROUTE_NAMES.BLOG_DETAIL,
    params: { id: relatedBlog.id },
  });
}
</script>

<template>
  <div
    v-if="prevBlog || nextBlog"
    class="h-[184px] bg-secondary border-t border-ternary flex divide-x py-[10px] md:px-2 xl:px-[10px] mb-16 xl:mb-0"
  >
    <!-- LEFT PART -->
    <div
      class="flex-1 flex items-center"
      :class="isHidedButtons ? 'justify-end' : 'justify-between'"
      v-if="prevBlog !== null"
    >
      <!-- PREV BUTTON -->
      <div
        v-show="!isHidedButtons"
        class="bg-ternary w-10 h-full rounded hidden md:flex items-center justify-center hover:cursor-pointer"
      >
        <svg
          width="11"
          height="22"
          viewBox="0 0 11 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10 1L1 11L10 21"
            stroke="#29160E"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
      <!-- PREV NEWS -->
      <div class="px-2 md:pr-6 xl:pr-8 flex flex-col text-left">
        <p
          class="text-muted sf-pro font-semibold text-base leading-[21px] xl:text-xl xl:leading-[28px] mb-5"
        >
          {{ t("blog_detail.prev_article") }}
        </p>
        <div class="flex cursor-pointer" @click="openRelatedBlog(prevBlog)">
          <img
            :src="prevBlog.image_url"
            alt="Prev bolg image"
            class="h-20 w-[110px] rounded-[10px] hidden md:block object-cover"
          />
          <div class="md:ml-4">
            <div class="text-muted sf-pro-xs mb-2">
              {{ prevBlog.created_at }}
            </div>
            <div
              class="text-brown sf-pro font-semibold text-base leading-[21px] xl:leading-[24px]"
            >
              {{
                prevBlog.titles[langStore.current] ||
                prevBlog.titles[langStore.fallbackLang]
              }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- RIGHT PART -->
    <div
      class="flex-1 flex items-center justify-between"
      v-if="nextBlog !== null"
    >
      <!-- NEXT NEWS -->
      <div class="px-2 md:pl-6 xl:pl-8 flex flex-col text-left">
        <p
          class="text-muted sf-pro font-semibold text-base leading-[21px] xl:text-xl xl:leading-[28px] mb-5"
        >
          {{ t("blog_detail.next_article") }}
        </p>
        <div class="flex cursor-pointer" @click="openRelatedBlog(nextBlog)">
          <img
            :src="nextBlog.image_url"
            alt="Prev bolg image"
            class="h-20 w-[110px] rounded-[10px] hidden md:block object-cover"
          />
          <div class="md:ml-4">
            <div class="text-muted sf-pro-xs mb-2">
              {{ nextBlog.created_at }}
            </div>
            <div
              class="text-brown sf-pro font-semibold text-base leading-[21px] xl:leading-[24px]"
            >
              {{
                nextBlog.titles[langStore.current] ||
                nextBlog.titles[langStore.fallbackLang]
              }}
            </div>
          </div>
        </div>
      </div>
      <!-- NEXT BUTTON -->
      <div
        v-show="!isHidedButtons"
        class="bg-ternary w-10 h-full rounded hidden md:flex items-center justify-center hover:cursor-pointer"
      >
        <svg
          width="11"
          height="22"
          viewBox="0 0 11 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 1L10 11L1 21"
            stroke="#29160E"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>
  </div>
</template>
